import { Link } from "react-router-dom";
import {
    ArrowRight,
    ChevronDown,
    Clock3,
    Leaf,
    MapPin,
    Search as SearchIcon,
    ShieldCheck,
    Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TSGoLogo } from "@/components/brand/TSGoLogo";
import { HeroIllustration } from "@/components/brand/HeroIllustration";
import { ROUTES } from "@/constants/routes";
import { brand } from "@/design-system/tokens";

/**
 * Trust badges shown below the hero.
 * Order matters: delivery speed first (strongest promise), then quality,
 * then value, then safety.
 */
const TRUST_BADGES: Array<{
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    subtitle: string;
    accent: string;
}> = [
    {
        icon: Clock3,
        title: "10–20 min",
        subtitle: "Lightning delivery",
        accent: "bg-brand/10 text-brand",
    },
    {
        icon: Leaf,
        title: "Fresh daily",
        subtitle: "Handpicked quality",
        accent: "bg-emerald-500/10 text-emerald-600",
    },
    {
        icon: Tag,
        title: "Best prices",
        subtitle: "Everyday low rates",
        accent: "bg-accentbrand/20 text-accentbrand-800",
    },
    {
        icon: ShieldCheck,
        title: "Secure pay",
        subtitle: "Encrypted checkout",
        accent: "bg-indigo-500/10 text-indigo-600",
    },
];

/**
 * WelcomeScreen — premium landing composition.
 *
 *  ┌─────────────────────────────────────────────────────────┐
 *  │  Logo                                Location pill       │
 *  ├─────────────────────────────────────────────────────────┤
 *  │  [eyebrow badge]                                         │
 *  │  Big headline (Poppins 800)      ┐                       │
 *  │  Supporting subheadline          │   Hero illustration   │
 *  │  Large search bar                │   (grocery bag SVG)   │
 *  │  Primary + secondary CTAs        ┘                       │
 *  ├─────────────────────────────────────────────────────────┤
 *  │  Trust badges (4-up on md+, 2×2 on mobile)               │
 *  └─────────────────────────────────────────────────────────┘
 */
export default function WelcomeScreen() {
    return (
        <div
            data-testid="welcome-screen"
            className="relative min-h-screen overflow-hidden bg-background text-foreground"
        >
            {/* Ambient background */}
            <div className="pointer-events-none absolute inset-0" aria-hidden>
                <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand/15 blur-3xl" />
                <div className="absolute -left-16 top-40 h-72 w-72 rounded-full bg-accentbrand/25 blur-3xl" />
                <div className="absolute bottom-0 right-1/3 h-64 w-64 rounded-full bg-brand/10 blur-3xl" />
            </div>

            {/* Top bar */}
            <header className="relative z-10">
                <div className="tsgo-container flex items-center justify-between gap-3 pt-5 sm:pt-7">
                    <div className="animate-tsgo-rise [animation-delay:60ms]" data-testid="welcome-logo">
                        <TSGoLogo size="md" />
                    </div>

                    <button
                        type="button"
                        data-testid="welcome-location-selector"
                        className="group inline-flex items-center gap-2 rounded-full border border-border/70 bg-white/70 px-3 py-2 text-left shadow-soft backdrop-blur transition hover:border-brand/40 hover:bg-white sm:gap-3 sm:px-4 animate-tsgo-rise [animation-delay:120ms]"
                    >
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand/10 text-brand">
                            <MapPin className="h-4 w-4" />
                        </span>
                        <span className="flex min-w-0 flex-col leading-tight">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Deliver to
                            </span>
                            <span className="truncate text-sm font-semibold text-foreground">
                                Detect my location
                            </span>
                        </span>
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition group-hover:text-foreground" />
                    </button>
                </div>
            </header>

            {/* Hero */}
            <main className="relative z-10">
                <section className="tsgo-container grid items-center gap-10 pb-16 pt-10 md:grid-cols-2 md:gap-12 md:pt-14 lg:gap-20 lg:pb-24 lg:pt-20">
                    {/* Copy column */}
                    <div className="max-w-xl">
                        <span
                            data-testid="welcome-eyebrow"
                            className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand animate-tsgo-rise [animation-delay:180ms]"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-70" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand" />
                            </span>
                            Now delivering in your city
                        </span>

                        <h1
                            data-testid="welcome-heading"
                            className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl animate-tsgo-rise [animation-delay:260ms]"
                        >
                            {brand.tagline.split(".")[0]}.
                            <br />
                            <span className="relative inline-block">
                                <span className="relative z-10 bg-gradient-to-r from-brand to-brand-700 bg-clip-text text-transparent">
                                    Delivered Fast.
                                </span>
                                <span
                                    aria-hidden
                                    className="absolute -bottom-1 left-0 right-0 h-2 rounded-full bg-accentbrand/60"
                                />
                            </span>
                        </h1>

                        <p
                            data-testid="welcome-subheading"
                            className="mt-5 max-w-lg text-base text-muted-foreground sm:text-lg animate-tsgo-rise [animation-delay:340ms]"
                        >
                            Groceries, essentials & daily needs — sourced fresh
                            and delivered to your door in minutes. Skip the
                            queue, skip the traffic, skip the wait.
                        </p>

                        {/* Search bar */}
                        <div
                            data-testid="welcome-search"
                            className="mt-8 animate-tsgo-rise [animation-delay:420ms]"
                        >
                            <label htmlFor="welcome-search-input" className="sr-only">
                                Search TSGo
                            </label>
                            <div className="group relative">
                                <SearchIcon className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground transition group-focus-within:text-brand" />
                                <input
                                    id="welcome-search-input"
                                    data-testid="welcome-search-input"
                                    type="search"
                                    placeholder='Search "milk", "bread", "chips"…'
                                    disabled
                                    className="h-14 w-full rounded-full border border-border/70 bg-white/85 pl-14 pr-32 text-base font-medium text-foreground shadow-soft outline-none backdrop-blur transition placeholder:text-muted-foreground focus:border-brand focus:ring-4 focus:ring-brand/20 sm:h-16 sm:text-lg"
                                />
                                <button
                                    type="button"
                                    disabled
                                    data-testid="welcome-search-submit"
                                    className="absolute right-1.5 top-1/2 hidden -translate-y-1/2 items-center gap-1.5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-brand transition hover:bg-brand-600 sm:inline-flex"
                                >
                                    Search
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                            <p className="mt-2 pl-2 text-xs text-muted-foreground">
                                Popular:{" "}
                                <span className="font-medium text-foreground/70">
                                    Milk · Bread · Fruits · Snacks · Personal care
                                </span>
                            </p>
                        </div>

                        {/* CTAs */}
                        <div className="mt-8 flex flex-col gap-3 sm:flex-row animate-tsgo-rise [animation-delay:500ms]">
                            <Button
                                asChild
                                size="lg"
                                data-testid="welcome-cta-signup"
                                className="h-12 rounded-full bg-brand px-6 text-base font-semibold text-white shadow-brand transition hover:-translate-y-0.5 hover:bg-brand-600 hover:shadow-elevated"
                            >
                                <Link to={ROUTES.signup}>
                                    Get started
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                            <Button
                                asChild
                                size="lg"
                                variant="outline"
                                data-testid="welcome-cta-login"
                                className="h-12 rounded-full border-border bg-white/70 px-6 text-base font-semibold text-foreground backdrop-blur transition hover:border-brand/40 hover:text-brand"
                            >
                                <Link to={ROUTES.login}>I already have an account</Link>
                            </Button>
                        </div>
                    </div>

                    {/* Illustration column */}
                    <div className="relative order-first md:order-last animate-tsgo-rise [animation-delay:220ms]">
                        <HeroIllustration className="max-w-[380px] sm:max-w-[460px] md:max-w-none" />
                    </div>
                </section>

                {/* Trust badges */}
                <section
                    data-testid="welcome-trust-badges"
                    aria-label="Why TSGo"
                    className="tsgo-container pb-16"
                >
                    <div className="rounded-3xl border border-border/60 bg-white/70 p-4 shadow-soft backdrop-blur sm:p-6 animate-tsgo-rise [animation-delay:600ms]">
                        <ul className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
                            {TRUST_BADGES.map(({ icon: Icon, title, subtitle, accent }, i) => (
                                <li
                                    key={title}
                                    data-testid={`trust-badge-${title.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="group flex items-center gap-3 rounded-2xl bg-background/60 p-3 transition hover:-translate-y-0.5 hover:bg-background hover:shadow-soft sm:p-4"
                                    style={{ animationDelay: `${640 + i * 80}ms` }}
                                >
                                    <span
                                        className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl transition group-hover:scale-105 ${accent}`}
                                    >
                                        <Icon className="h-5 w-5" />
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-foreground sm:text-base">
                                            {title}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {subtitle}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </main>

            {/* Footer micro-legal */}
            <footer className="relative z-10 pb-8 text-center text-xs text-muted-foreground">
                By continuing, you agree to our{" "}
                <span className="font-medium text-foreground/70">Terms</span> &{" "}
                <span className="font-medium text-foreground/70">Privacy Policy</span>.
            </footer>
        </div>
    );
}
